export type TelegramButtonStyle = "default" | "primary" | "success" | "danger";

export type EditableLayoutButton = {
  id: string;
  text: string;
  url: string;
  style: TelegramButtonStyle;
  customEmojiId?: string;
  emojiFallback?: string;
};

export type EditableButtonRow = {
  id: string;
  buttons: EditableLayoutButton[];
};

export type ButtonPosition = { row: number; button: number };
export type ButtonLayoutLimits = { total: number; perRow: number };
export const DEFAULT_BUTTON_LAYOUT_LIMITS: ButtonLayoutLimits = { total: 100, perRow: 8 };

const cloneRows = (rows: readonly EditableButtonRow[]): EditableButtonRow[] =>
  rows.map((row) => ({ ...row, buttons: row.buttons.map((button) => ({ ...button })) }));

const buttonCount = (rows: readonly EditableButtonRow[]) =>
  rows.reduce((total, row) => total + row.buttons.length, 0);

const httpUrl = (value: string) => {
  const candidate = value.trim();
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:"
      ? candidate
      : null;
  } catch {
    return null;
  }
};

export const canonicalButtonRows = (
  rows: readonly EditableButtonRow[],
  limits: ButtonLayoutLimits = DEFAULT_BUTTON_LAYOUT_LIMITS
): EditableButtonRow[] => {
  let total = 0;
  return rows.flatMap((row) => {
    const buttons = row.buttons.slice(0, limits.perRow).flatMap((button) => {
      if (total >= limits.total) return [];
      const text = button.text.trim();
      const url = httpUrl(button.url);
      if (!text || !url) return [];
      total += 1;
      const customEmojiId = button.customEmojiId?.trim();
      const emojiFallback = customEmojiId ? button.emojiFallback?.trim() : undefined;
      return [{
        id: button.id,
        text: text.slice(0, 64),
        url,
        style: button.style,
        ...(customEmojiId ? { customEmojiId } : {}),
        ...(emojiFallback ? { emojiFallback } : {})
      }];
    });
    return buttons.length ? [{ id: row.id, buttons }] : [];
  });
};

export const addRow = (
  rows: readonly EditableButtonRow[],
  row: EditableButtonRow,
  limits: ButtonLayoutLimits = DEFAULT_BUTTON_LAYOUT_LIMITS
): EditableButtonRow[] => {
  if (!row.buttons.length || buttonCount(rows) + row.buttons.length > limits.total || row.buttons.length > limits.perRow) {
    return cloneRows(rows);
  }
  return [...cloneRows(rows), { ...row, buttons: row.buttons.map((button) => ({ ...button })) }];
};

export const removeRow = (
  rows: readonly EditableButtonRow[],
  rowIndex: number
): EditableButtonRow[] => rows.filter((_, index) => index !== rowIndex)
  .map((row) => ({ ...row, buttons: row.buttons.map((button) => ({ ...button })) }));

export const moveRow = (
  rows: readonly EditableButtonRow[],
  from: number,
  to: number
): EditableButtonRow[] => {
  const next = cloneRows(rows);
  if (from === to || from < 0 || to < 0 || from >= next.length || to >= next.length) return next;
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
};

export const addButton = (
  rows: readonly EditableButtonRow[],
  rowIndex: number,
  button: EditableLayoutButton,
  limits: ButtonLayoutLimits = DEFAULT_BUTTON_LAYOUT_LIMITS
): EditableButtonRow[] => {
  const next = cloneRows(rows);
  const row = next[rowIndex];
  if (!row || row.buttons.length >= limits.perRow || buttonCount(next) >= limits.total) return next;
  row.buttons.push({ ...button });
  return next;
};

export const removeButton = (
  rows: readonly EditableButtonRow[],
  position: ButtonPosition
): EditableButtonRow[] => {
  const next = cloneRows(rows);
  const row = next[position.row];
  if (!row || position.button < 0 || position.button >= row.buttons.length) return next;
  row.buttons.splice(position.button, 1);
  if (row.buttons.length === 0) next.splice(position.row, 1);
  return next;
};

export const moveButton = (
  rows: readonly EditableButtonRow[],
  from: ButtonPosition,
  to: ButtonPosition
): EditableButtonRow[] => {
  const next = cloneRows(rows);
  const source = next[from.row];
  const target = next[to.row];
  if (
    !source || !target ||
    from.button < 0 || from.button >= source.buttons.length ||
    to.button < 0 || to.button > target.buttons.length ||
    (from.row !== to.row && target.buttons.length >= 8)
  ) return next;

  const [button] = source.buttons.splice(from.button, 1);
  const insertion = from.row === to.row && from.button < to.button
    ? to.button - 1
    : to.button;
  target.buttons.splice(insertion, 0, button);
  if (source.buttons.length === 0) next.splice(from.row, 1);
  return next;
};
